import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, TrendingUp, Quote, ChevronRight, Award } from "lucide-react";
import { fadeUp, stagger, AnimatedSection } from "@/lib/animations";

export default function ResultsPage() {
  const caseStudies = [
    {
      id: "1",
      title: "Da invisibili al primo posto nella ricerca locale",
      org_name: "Fondazione Speranza",
      category: "Fondi Marketing + SEO",
      description: "La Fondazione Speranza faticava a raggiungere nuovi donatori online. Con la nostra gestione dei fondi per il marketing e per le AI e la strategia SEO, sono passati dalla pagina 5 al primo risultato di ricerca per la loro causa nella loro città.",
      quote: "Givenda ha trasformato la nostra presenza online. Siamo passati da 200 visitatori al mese a oltre 4.000. Solo i fondi marketing hanno portato 28.000€ in pubblicità gratuita.",
      quote_author: "Sara Bianchi, Direttrice Esecutiva",
      metrics: [
        { label: "Traffico Web", before: "200/mese", after: "4.200/mese" },
        { label: "Spesa Ads Marketing", before: "0€", after: "28K€ gratis" },
        { label: "Donazioni Online", before: "1.200€/mese", after: "8.400€/mese" },
        { label: "Posizione Ricerca", before: "Pagina 5", after: "#1" }
      ],
      image: "https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800"
    },
    {
      id: "2",
      title: "Raddoppio delle iscrizioni volontari in 90 giorni",
      org_name: "Alleanza Terra Verde",
      category: "Sito Web + Comunità",
      description: "Alleanza Terra Verde aveva bisogno di un sito web che convertisse i visitatori in volontari. Abbiamo ricostruito il loro sito e la piattaforma comunitaria, raddoppiando le iscrizioni nel primo trimestre.",
      quote: "Il nuovo sito web e l'hub comunitario hanno cambiato tutto per noi. Le persone restano, interagiscono e si iscrivono. È stata una svolta.",
      quote_author: "Marco Verdi, Direttore di Programma",
      metrics: [
        { label: "Iscrizioni Volontari", before: "45/mese", after: "112/mese" },
        { label: "Membri Comunità", before: "320", after: "1.800+" },
        { label: "Frequenza di Rimbalzo", before: "72%", after: "31%" },
        { label: "Presenze Eventi", before: "60 media", after: "180 media" }
      ],
      image: "https://images.unsplash.com/photo-1593113598332-cd288d649433?w=800"
    },
    {
      id: "3",
      title: "120.000€ in fondi marketing gratuiti per il soccorso animali",
      org_name: "Zampe & Amore",
      category: "Fondi Marketing",
      description: "Zampe & Amore non aveva mai sentito parlare dei fondi per il marketing e per le AI. Li abbiamo aiutati a fare domanda, ottenere l'approvazione e ora gestiamo 10.000€/mese in pubblicità gratuita per i loro programmi di adozione.",
      quote: "Non avevamo idea di poter accedere a fondi marketing gratuiti. Givenda ha gestito tutto e ora raggiungiamo migliaia di potenziali adottanti ogni mese senza spendere un centesimo in pubblicità.",
      quote_author: "Lisa Rossi, Fondatrice",
      metrics: [
        { label: "Budget Ads Mensile", before: "0€", after: "10K€ gratis" },
        { label: "Richieste Adozione", before: "30/mese", after: "145/mese" },
        { label: "Visitatori Sito", before: "800/mese", after: "6.200/mese" },
        { label: "Adozioni", before: "12/mese", after: "38/mese" }
      ],
      image: "https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=800"
    }
  ];

  const stats = [
    { value: "500+", label: "Nonprofit Servite", sub: "In tutta Italia" },
    { value: "9+", label: "Anni di Impatto", sub: "Dal 2016" },
    { value: "15M€", label: "In Fondi Gestiti", sub: "Ads gratuiti per le nonprofit" },
    { value: "92%", label: "Tasso di Fidelizzazione", sub: "Restano perché funziona" },
  ];

  return (
    <div data-testid="results-page">
      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="results-hero">
        <div className="hero-glow absolute inset-0 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center max-w-3xl mx-auto">
            <motion.div variants={fadeUp}>
              <Badge className="bg-[#D85A30]/10 text-[#D85A30] border-[#D85A30]/20 hover:bg-[#D85A30]/15 mb-6 px-4 py-1.5 rounded-full">
                <span className="kicker">Risultati Reali</span>
              </Badge>
            </motion.div>
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#2C2C2A] leading-[1.1] mb-6">
              Scopri cosa è possibile quando la tua nonprofit{" "}
              <span className="text-[#D85A30]">viene trovata.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base md:text-lg text-[#888780] leading-relaxed">
              Casi studio reali da nonprofit reali. Questi sono i risultati che otteniamo ogni giorno.
            </motion.p>
          </AnimatedSection>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#F1EFE8]/40 border-y border-[#D9D8D2]/50" data-testid="results-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <motion.div key={i} variants={fadeUp} className="text-center" data-testid={`results-stat-${i}`}>
                <p className="text-3xl md:text-4xl font-bold text-[#2C2C2A] mb-1" style={{ fontFamily: "'Fraunces', serif" }}>{s.value}</p>
                <p className="text-sm font-semibold text-[#2C2C2A]">{s.label}</p>
                <p className="text-xs text-[#888780]">{s.sub}</p>
              </motion.div>
            ))}
          </AnimatedSection>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 md:py-32" data-testid="results-case-studies">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="mb-16">
            <motion.p variants={fadeUp} className="kicker text-[#D85A30] mb-3">Casi Studio</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-semibold text-[#2C2C2A] leading-tight">
              Storie di successo in evidenza
            </motion.h2>
          </AnimatedSection>

          <div className="space-y-16">
            {caseStudies.map((study, i) => (
              <AnimatedSection key={study.id}>
                <motion.div variants={fadeUp}>
                  <Card className="bg-white border-[#D9D8D2]/50 shadow-[0_10px_30px_-10px_rgba(8,80,65,0.05)] overflow-hidden" data-testid={`case-study-${i}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                      <div className="relative h-[300px] lg:h-auto">
                        <img
                          src={study.image}
                          alt={study.org_name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-[#085041]/20 to-transparent" />
                      </div>
                      <CardContent className="p-8 lg:p-12 flex flex-col justify-center">
                        <Badge className="bg-[#085041]/5 text-[#2C2C2A] border-none w-fit mb-4 rounded-full hover:bg-[#085041]/10">
                          {study.category}
                        </Badge>
                        <h3 className="text-2xl font-semibold text-[#2C2C2A] mb-2">{study.org_name}</h3>
                        <p className="text-sm text-[#888780] leading-relaxed mb-6">{study.description}</p>

                        <div className="bg-[#F1EFE8]/60 rounded-xl p-5 mb-6">
                          <Quote className="w-6 h-6 text-[#D85A30]/30 mb-2" />
                          <p className="text-sm text-[#2C2C2A] italic leading-relaxed mb-2">{study.quote}</p>
                          <p className="text-xs text-[#888780] font-medium">— {study.quote_author}</p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          {study.metrics.map((m, j) => (
                            <div key={j} className="bg-white border border-[#D9D8D2]/50 rounded-xl p-3 text-center">
                              <p className="text-xs text-[#888780] mb-0.5">{m.label}</p>
                              <div className="flex items-center justify-center gap-1.5">
                                <span className="text-xs text-[#888780] line-through">{m.before}</span>
                                <ChevronRight className="w-3 h-3 text-[#D85A30]" />
                                <span className="text-sm font-bold text-[#2C2C2A]">{m.after}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#085041]" data-testid="results-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection className="max-w-2xl mx-auto">
            <motion.div variants={fadeUp} className="flex items-center justify-center mb-6">
              <Award className="w-12 h-12 text-[#D85A30]" />
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-semibold text-white leading-tight mb-6">
              La tua nonprofit potrebbe essere la nostra prossima storia di successo
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/60 mb-8 text-base">
              Unisciti a oltre 500 organizzazioni che hanno trasformato la loro presenza digitale con Givenda.
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link to="/book-a-call">
                <Button className="rounded-full px-8 h-12 text-base bg-[#D85A30] hover:bg-[#C04E25] text-white font-medium shadow-lg transition-all hover:-translate-y-1" data-testid="results-cta-button">
                  Inizia la Tua Revisione Gratuita
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
